'use client'

import { useEffect, useRef } from 'react'
import posthog from 'posthog-js'
import { createClient } from '@/lib/supabase/client'
import { getAnalyticsMode, onAnalyticsModeChange } from '@/lib/analytics/consent'
import {
  applySignedInAnswer,
  applySignedOut,
  readAccountAnswer,
  setSignedInUser,
} from '@/lib/analytics/account-consent'

/**
 * Applies the signed-in member's analytics answer and identifies them in
 * PostHog so sessions are linked to real persons. Rendered once in the root
 * layout.
 *
 * On every page load and sign-in it reads the account's answer (Settings →
 * Privacy & Consent) and switches PostHog to it (lib/analytics/account-consent.ts):
 * signed in, the account decides, in the app and on the web. On sign-out the
 * web banner (or, in the app, nothing) decides again.
 *
 * Identifies only in `full` mode and only once the account's answer is
 * known, so a device that starts full from an old answer never identifies
 * someone who has since said no. Going back to anonymous has already reset
 * PostHog, so the next identify starts over.
 *
 * Only the Supabase user id is sent — no email, no name, no other person
 * properties (measurement wave 1: PostHog is a third party and doesn't need
 * PII; the school and role travel as super properties from <AnalyticsContext>).
 * Person properties set by older builds stay on PostHog until cleaned there.
 */
export function PostHogIdentify() {
  const identifiedRef = useRef<string | null>(null)

  useEffect(() => {
    const supabase = createClient()
    // `undefined` until the first answer from auth, then the user id or null.
    let userId: string | null | undefined = undefined
    // The user whose account answer has been applied.
    let resolvedFor: string | null = null
    let run = 0

    function sync() {
      if (getAnalyticsMode() !== 'full') {
        identifiedRef.current = null
        return
      }
      if (userId && userId === resolvedFor && userId !== identifiedRef.current) {
        posthog.identify(userId)
        identifiedRef.current = userId
      } else if (!userId && identifiedRef.current) {
        posthog.reset()
        identifiedRef.current = null
      }
    }

    async function onUser(next: string | null) {
      // Token refreshes and the duplicate initial event change nothing.
      if (next === userId) return
      userId = next
      setSignedInUser(next)
      const current = ++run

      if (!next) {
        resolvedFor = null
        sync()
        applySignedOut(posthog)
        return
      }

      const answer = await readAccountAnswer(supabase, next)
      if (current !== run) return
      // Unreadable: keep the mode this load started in, identify nobody.
      if (!answer) return
      applySignedInAnswer(posthog, next, answer)
      resolvedFor = next
      sync()
    }

    supabase.auth.getUser().then(({ data: { user } }) => onUser(user?.id ?? null))

    // Re-check on auth state changes (login, logout)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void onUser(session?.user?.id ?? null)
    })

    const unsubscribeMode = onAnalyticsModeChange(sync)

    return () => {
      subscription.unsubscribe()
      unsubscribeMode()
    }
  }, [])

  return null
}
